import { useQuery, useTeam } from '@vrplatform/data';
import { type Maybe, day } from '@vrplatform/utils';
import { useDebouncedValue } from '@mantine/hooks';
import type { owner_statement } from '@vrplatform/graphql';
import {
  formatOwnerName,
  getListingAddress,
  getListingDisabledAutomations,
  getListingName,
  whereListingPeriod,
} from '@vrplatform/ui-common';
import { getListingDeactivation } from '../detail/useListingDetailViewQuery';

export function useListingDetailDrawerQuery(id: Maybe<string>) {
  const [{ id: teamId }] = useTeam();

  const query = useQuery(
    (q, args) => {
      if (!args.id)
        return {
          listing: null,
        };

      return q
        .listings({
          where: {
            id: { _eq: args.id },
          },
        })
        .map((listing) => {
          const { disabledAutomations } =
            getListingDisabledAutomations(listing);

          const currentPeriod = listing
            .ownershipPeriods({
              limit: 1,
              where: whereListingPeriod({
                date: day().yyyymmdd(),
                type: 'ownership',
              }),
            })
            .map((period) => ({
              id: period.id,
              startAt: period.startAt!,
              endAt: period.endAt,
              businessModel: period.businessModel ?? 'managed',
              members: period
                .members({
                  order_by: [
                    {
                      contact: {
                        name: 'asc_nulls_last',
                      },
                    },
                  ],
                })
                .map((owner) => ({
                  id: owner.id as string,
                  ownerId: owner.contactId as string,
                  ownerName: formatOwnerName(owner.contact),
                  ownerSplitSuperBasePoints: owner.split,
                  ownerType: owner.contact?.companyType || 'individual',
                })),
            }))[0];

          const listingConnections = [
            {
              id: listing.connectionId,
              name: listing.connection?.name,
              icon: listing.connection?.app.iconRound,
              app: listing.connection?.app.name,
            },
          ];

          const getStatement = (st: owner_statement) => ({
            id: st.id,
            periodId: st.listingOwnershipPeriodId,
            startAt: st.startAt,
            status: st.status,
            owners: st.listingOwnershipPeriod?.members().map((member) => ({
              id: member.contactId,
              name: formatOwnerName(member.contact, {
                lastNameFirst: false,
              }),
            })),
          });

          return {
            listing: {
              id: listing.id,
              address: getListingAddress(listing),
              currentPeriod,
              addressId: listing.address_id,
              status: listing.status,
              deactivation: getListingDeactivation(listing, args.today),
              sourceStatus: listing.source?.status,
              currency: listing.defaultCurrency,
              pmsStatus: listing.pmsStatus,
              name: getListingName(listing),
              collection: {
                id: listing.collectionId,
                name: listing.collection?.name || '',
              },
              pmsName: listing.name,
              title: listing.title,
              disabledAutomations,
              tenantId: teamId,
              isPmsListing: !!listing.connectionId,
              ownerStatements: q
                .ownerStatements({
                  order_by: [{ startAt: 'desc_nulls_last' }],
                  where: {
                    status: { _eq: 'published' },
                    listingOwnershipPeriod: {
                      listingId: { _eq: args.id },
                    },
                  },
                })
                .map(getStatement),
              listingConnections,
            },
          };
        })[0];
    },
    {
      skip: !id,
      queryKey: 'listings',
      variables: {
        id,
        teamId,
        today: day().yyyymmdd(),
      },
    }
  );

  const [debounced] = useDebouncedValue(query.data, 500);

  return { ...query, data: query.data || debounced };
}

export type Listing = NonNullable<
  NonNullable<ReturnType<typeof useListingDetailDrawerQuery>['data']>['listing']
>;
